import { AlertTriangle, Copy } from 'lucide-react'
import { IconButton } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/Badge'
import { dateTime, truncateMiddle } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { Verdict } from '@/types'

/**
 * Verdicts are immutable and bound to the entity hash they were cast against.
 * Showing the history is what makes that legible: without it, a re-appearing
 * rejection looks like the system lost the verdict.
 */
export function VerdictHistory({
  verdicts,
  entityHash,
}: {
  verdicts: Verdict[]
  entityHash: string
}) {
  return (
    <div className="space-y-2">
      <h3 className="field-label">Review history</h3>

      {verdicts.length === 0 ? (
        <p className="text-[13px] text-ink-faint">Not reviewed yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {[...verdicts].reverse().map((v, i) => (
            <li key={`${v.at}-${i}`} className="flex flex-wrap items-center gap-2 text-[12px]">
              <StatusBadge status={v.verdict} />
              <span className="text-ink-muted">{v.by}</span>
              <span className="text-ink-faint">{dateTime(v.at)}</span>
              <span
                className="font-mono text-[11px] text-ink-faint"
                title={`Entity hash ${v.entityHash}`}
              >
                {truncateMiddle(v.entityHash, 11)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-1.5 pt-0.5">
        <span className="text-[11px] text-ink-faint">Current hash</span>
        <code className="font-mono text-[11px] text-ink-muted">
          {truncateMiddle(entityHash, 16)}
        </code>
        <IconButton
          label="Copy entity hash"
          className="h-5 w-5"
          onClick={() => navigator.clipboard?.writeText(entityHash)}
        >
          <Copy size={10} aria-hidden />
        </IconButton>
      </div>
    </div>
  )
}

/**
 * The "content changed" marker. A re-crawl gives a changed version of a
 * rejected item a new hash, so it re-enters as pending — correct, but it
 * reads as a system failure without this label.
 */
export function PreviouslyRejectedFlag({
  compact = false,
  className,
}: {
  compact?: boolean
  className?: string
}) {
  if (compact) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-[3px] bg-warn-50 px-1.5 py-0.5',
          'text-[10px] font-head font-semibold text-warn',
          className,
        )}
        title="This was rejected before. The content has changed since."
      >
        <AlertTriangle size={9} aria-hidden />
        Content changed
      </span>
    )
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-[4px] bg-warn-50 px-3 py-2 text-[13px] leading-5 text-warn',
        className,
      )}
    >
      <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
      <p>
        <strong className="font-head font-semibold">Previously rejected — content changed.</strong>{' '}
        The last crawl produced a different version of this item, so it is back in the queue. The
        earlier verdict still applies to the earlier content.
      </p>
    </div>
  )
}
